import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radio, ArrowRight, Users } from "lucide-react";
import { getVenueDirectory } from "@/lib/api";
import { isAirportVenue } from "@/lib/useLiveAirports";
import LiveRoomModal from "@/components/LiveRoomModal";

/**
 * BetaTonightTicker — a small, honest "how alive is GateMate right now" strip.
 * When rooms are live, each venue name becomes a tappable chip that opens
 * a LiveRoomModal with the GateLink code + a one-tap TestFlight CTA. When
 * nothing is live it degrades into a quiet "next check-in window" nudge.
 */
export default function BetaTonightTicker() {
  const [state, setState] = useState({ loaded: false, live_venues: 0, live_here: 0, sample: [], airports: [] });
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancel = false;
    const load = () => {
      getVenueDirectory()
        .then((list) => {
          if (cancel) return;
          const live = (list || []).filter((v) => (v.live_now || 0) > 0);
          // Airport rooms are the always-on fallback proof: they show when no
          // public rooms are live; if those are closed too, the section hides.
          const airports = live.filter(isAirportVenue);
          const publicLive = live.filter((v) => !isAirportVenue(v));
          const sample = publicLive
            .sort((a, b) => (b.live_now || 0) - (a.live_now || 0))
            .slice(0, 4);
          const total_here = publicLive.reduce((sum, v) => sum + (v.live_now || 0), 0);
          setState({
            loaded: true,
            live_venues: publicLive.length,
            live_here: total_here,
            sample,
            airports,
          });
        })
        .catch(() => setState((s) => ({ ...s, loaded: true })));
    };
    load();
    const t = setInterval(load, 45000);
    return () => {
      cancel = true;
      clearInterval(t);
    };
  }, []);

  if (!state.loaded) return null;

  const anyLive = state.live_venues > 0;
  const airportOnly = !anyLive && state.airports.length > 0;
  const active = anyLive || airportOnly;
  // No public rooms AND no airport rooms open → hide the section entirely.
  if (!active) return null;

  return (
    <>
      <section className="relative py-10 md:py-14" data-testid="beta-tonight-ticker">
        <div className="container-x">
          <div
            className={`glass rounded-2xl px-5 md:px-6 py-4 md:py-5 flex flex-col md:flex-row md:items-center gap-4 ring-1 ${
              active ? "ring-neon-cyan/30" : "ring-white/5"
            }`}
          >
            {/* Status pill */}
            <div className="flex items-center gap-3 shrink-0">
              <span className="relative flex h-2.5 w-2.5">
                {active && (
                  <span className="absolute inline-flex h-full w-full rounded-full bg-neon-cyan opacity-70 animate-ping" />
                )}
                <span
                  className={`relative inline-flex h-2.5 w-2.5 rounded-full ${
                    active ? "bg-neon-cyan" : "bg-zinc-500"
                  }`}
                />
              </span>
              <span
                className={`text-[11px] uppercase tracking-[0.24em] font-semibold flex items-center gap-1.5 ${
                  active ? "text-neon-cyan" : "text-zinc-400"
                }`}
              >
                <Radio className="w-3.5 h-3.5" />
                {anyLive ? "Live right now" : "Live at the airports"}
              </span>
            </div>

            {/* Middle content */}
            <div className="flex-1 min-w-0">
              {anyLive ? (
                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                  <span className="text-white font-semibold text-sm md:text-base flex items-center gap-1.5 shrink-0">
                    <Users className="w-4 h-4 text-neon-cyan" />
                    <span data-testid="beta-tonight-people" className="tabular-nums">
                      {state.live_here}
                    </span>{" "}
                    checked in across{" "}
                    <span data-testid="beta-tonight-venues" className="tabular-nums">
                      {state.live_venues}
                    </span>{" "}
                    {state.live_venues === 1 ? "room" : "rooms"}
                  </span>
                  {/* Tappable venue chips (deep-link into LiveRoomModal) */}
                  <div className="flex flex-wrap gap-1.5 min-w-0" data-testid="beta-tonight-chips">
                    {state.sample.map((v) => (
                      <button
                        key={v.id || v.slug}
                        type="button"
                        onClick={() => setSelected(v)}
                        data-testid={`beta-tonight-chip-${v.slug}`}
                        className="group inline-flex items-center gap-1.5 rounded-full glass hover:glass-cyan px-3 py-1 text-xs font-medium text-zinc-300 hover:text-white transition-all"
                      >
                        <span className="max-w-[10rem] truncate">{v.name}</span>
                        <span className="tabular-nums text-neon-cyan font-semibold">
                          {v.live_now}
                        </span>
                        <ArrowRight className="w-3 h-3 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-transform" />
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                  <span className="text-white font-semibold text-sm md:text-base shrink-0" data-testid="beta-tonight-airports">
                    Airport rooms are open
                  </span>
                  <div className="flex flex-wrap gap-1.5 min-w-0" data-testid="beta-tonight-airport-chips">
                    {state.airports.slice(0, 4).map((v) => (
                      <button
                        key={v.id || v.slug}
                        type="button"
                        onClick={() => setSelected(v)}
                        data-testid={`beta-tonight-chip-${v.slug}`}
                        className="group inline-flex items-center gap-1.5 rounded-full glass hover:glass-cyan px-3 py-1 text-xs font-medium text-zinc-300 hover:text-white transition-all"
                      >
                        <span className="max-w-[10rem] truncate">{v.name}</span>
                        <span className="tabular-nums text-neon-cyan font-semibold">
                          {v.live_now}
                        </span>
                        <ArrowRight className="w-3 h-3 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-transform" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Always-visible fallback CTA */}
            <Link
              to="/venues/directory"
              data-testid="beta-tonight-see-all"
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full glass hover:glass-cyan px-4 py-2 text-xs md:text-sm font-semibold text-neon-cyan hover:text-white transition-all"
            >
              Browse rooms <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      <LiveRoomModal
        venue={selected}
        open={!!selected}
        onOpenChange={(o) => {
          if (!o) setSelected(null);
        }}
      />
    </>
  );
}
